import type { SystemConfig } from "@/state/configState";
import type { LabwareType } from "@/automation/system-config/systemConfigParser";

export function getLabwareTypeDimensionString(
  labwareTypeId: string,
  activeConfig: SystemConfig
): string | null {
  const labwareTypeDefinition = activeConfig.labware?.find(
    (lw) => lw.id === labwareTypeId
  );
  if (!labwareTypeDefinition?.specifications) return null;

  const dimensionsSpec = labwareTypeDefinition.specifications.find(
    (spec: any) => spec.name === "dimensions"
  );
  if (dimensionsSpec?.value != null && dimensionsSpec.value !== "") {
    return String(dimensionsSpec.value);
  }

  // Fallback: construct from RowCount + ColumnCount (raw MDB column names)
  const rowSpec = labwareTypeDefinition.specifications.find(
    (spec: any) => spec.name === "RowCount"
  );
  const colSpec = labwareTypeDefinition.specifications.find(
    (spec: any) => spec.name === "ColumnCount"
  );
  if (rowSpec?.value != null && colSpec?.value != null) {
    const rows = Number(rowSpec.value);
    const cols = Number(colSpec.value);
    if (rows > 0 && cols > 0) return `${rows}x${cols}`;
  }

  return null;
}

export function parseDimensionString(
  dimString: string | null
): [number, number] | null {
  if (!dimString) return null;
  const match = String(dimString).match(/^(\d+)x(\d+)$/);
  if (match) {
    return [parseInt(match[1], 10), parseInt(match[2], 10)];
  }
  return null;
}

export function getLabwareDimensions(
  labwareTypeId: string,
  activeConfig: SystemConfig
): [number, number] | null {
  const dimensionString = getLabwareTypeDimensionString(
    labwareTypeId,
    activeConfig
  );
  if (!dimensionString) {
    return null;
  }
  return parseDimensionString(dimensionString);
}

/**
 * Reads a labware type's grid dimensions from its catalog specifications.
 * Primary: the Briefly-native "dimensions" spec ("8x12"); fallback: the raw
 * MDB "RowCount" / "ColumnCount" specs. Returns null when neither is present.
 */
export function getLabwareTypeDimensions(
  labwareType: LabwareType | undefined | null
): { rows: number; columns: number } | null {
  if (!labwareType?.specifications) return null;

  const dimensionsSpec = labwareType.specifications.find(
    (spec) => spec.name === "dimensions"
  );
  if (dimensionsSpec?.value) {
    const m = String(dimensionsSpec.value).match(/(\d+)x(\d+)/);
    if (m) return { rows: parseInt(m[1], 10), columns: parseInt(m[2], 10) };
  }

  const rowSpec = labwareType.specifications.find((s) => s.name === "RowCount");
  const colSpec = labwareType.specifications.find(
    (s) => s.name === "ColumnCount"
  );
  if (rowSpec?.value != null && colSpec?.value != null) {
    const rows = Number(rowSpec.value);
    const columns = Number(colSpec.value);
    if (rows > 0 && columns > 0) return { rows, columns };
  }

  return null;
}
